import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, usePropertyAddress, usePropertyAddressUrl, useSlotGroup, EditableCollectionBinding, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, stagger, telHref, titleCase, toParagraph } from '../utils/quinn';
import { useMediaQuery, useRawProperty } from '../utils/hooks';
import { tourCta, useSiteFlags } from '../utils/site';
import { DetailsRow, useTourModal } from '../utils/details';

interface Tour {
  virtual_tour_title?: string;
  virtual_tour_url?: string;
  virtual_tour_thumbnail?: string;
  virtual_tour_description?: string;
}

const PLAN_OUTLINE = (
  <svg className="fpcard__outline" viewBox="0 0 160 120" aria-hidden="true">
    <rect x="8" y="8" width="144" height="104" rx="4" />
    <path d="M8 60h64M72 8v104M72 60h80M112 60v52" />
    <path d="M40 8v22M40 40v20" strokeDasharray="3 5" />
  </svg>
);

const toursLead = 'm-0 max-w-[520px] pb-1.5 text-base leading-[26px] text-[--q-muted] max-[767px]:text-[15px] max-[767px]:leading-6';
const wayCopy = 'flex min-w-0 max-w-[340px] flex-col gap-1.5';
const wayTitle = 'm-0 font-heading text-[28px] font-light leading-[34px] [&_p]:m-0 max-[767px]:text-[24px] max-[767px]:leading-[30px]';
const wayLead = 'm-0 text-[15px] leading-6 text-[--q-muted] [&_p]:m-0';

const norm = (s: string | null | undefined) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** "Step inside the Jarvis and the Jefferson in 3D from home, then see one in person on a self-guided tour." */
function leadFor(names: string[] | null): string {
  const visit = 'on a self-guided tour';
  if (!names) return `See an available home in person, ${visit}.`;
  if (!names.length) return `Step inside a home in 3D from home, then see one in person ${visit}.`;
  const list = names.length === 1 ? `the ${names[0]}` : `${names.slice(0, -1).map((n) => `the ${n}`).join(', ')} and the ${names[names.length - 1]}`;
  return `Step inside ${list} in 3D from home, then see one in person ${visit}.`;
}

const slotGroup = (path: string): SlotGroupDefinition => contentSchema.slotGroups![path];

/** Tour your way: 3D walkthroughs of the plans beside the self-guided tour and the community's address. */
export function TourOptions() {
  const tourCtaDef = slotGroup('touroptions.tour_cta');
  const p = useRawProperty();
  const flags = useSiteFlags();
  const phone = useMediaQuery('(max-width: 767px)');
  const modal = useTourModal();
  const tours = useCollectionBinding<Tour>('touroptions.tours', { collection: 'virtual_tours' });
  const tour = useSlotGroup('touroptions.tour_cta', tourCtaDef);

  const plans = (p.floorplans as Array<{ floorplan_name?: string; floorplan_image_url?: string }>) || [];
  const featured = clean(p.property_featured_image);
  const cards = tours
    .filter((t) => clean(t.virtual_tour_url))
    .map((t) => {
      const name = titleCase(clean(t.virtual_tour_title) || '3D tour');
      const plan = plans.find((f) => norm(f.floorplan_name) === norm(name));
      const planImg = clean(plan?.floorplan_image_url);
      const photo = clean(t.virtual_tour_thumbnail);
      return {
        name,
        url: t.virtual_tour_url!,
        desc: clean(t.virtual_tour_description) || (plan ? `3D walkthrough of the ${name} home` : 'Walk through a home in 3D'),
        img: photo || planImg || featured,
        isPhoto: !!photo || (!planImg && !!featured),
        isPlan: !!plan,
      };
    });
  const tourLink = tourCta(tour, flags.selfTourUrl);
  const tel = telHref(flags.phone);
  const street = clean(p.property_address_1);
  const cityLine = [[clean(p.property_address_city), clean(p.property_address_state)].filter(Boolean).join(', '), clean(p.property_address_zip)].filter(Boolean).join(' ');
  const address = clean(usePropertyAddress()) || [street, cityLine].filter(Boolean).join(', ');
  const addressUrl = usePropertyAddressUrl();
  const leadDefault = toParagraph(leadFor(cards.length ? cards.filter((c) => c.isPlan).map((c) => c.name) : null));
  const rowSummary = [cards.length && '3D tours', 'self-guided'].filter(Boolean).join(' · ');

  const way = `flex flex-wrap items-start justify-between gap-6 border-b border-b-[--q-border] [&_.btn]:shrink-0 max-[767px]:flex-col max-[767px]:gap-3.5 max-[767px]:[&_.btn]:w-full ${
    phone ? 'py-5' : 'py-7 max-[767px]:py-[22px]'
  }`;

  const tiles = (
    <EditableCollectionBinding
      bindingPath="touroptions.tours"
      label="Virtual Tours"
      className={`shrink-0 max-[1199px]:[scrollbar-width:none] max-[767px]:[scroll-padding:0_var(--gutter)] max-[767px]:[scroll-snap-type:x_mandatory] max-[767px]:[&::-webkit-scrollbar]:hidden ${
        phone
          ? 'grid grid-cols-[1fr_1fr] gap-2 overflow-visible'
          : 'flex gap-6 max-[1199px]:overflow-x-auto max-[1199px]:overflow-y-hidden max-[1199px]:pb-2 max-[1199px]:pt-1 max-[767px]:mx-[calc(-1*var(--gutter))] max-[767px]:px-[--gutter] min-[1200px]:min-w-0 min-[1200px]:shrink min-[1200px]:flex-wrap'
      }`}
    >
      {cards.map((c, i) => (
        <a
          key={c.url}
          className={`group/vtcard flex flex-col overflow-hidden rounded-lg border border-[--q-border] bg-[--q-paper] text-inherit no-underline [transition:transform_var(--q-motion-normal)_var(--q-ease-out),box-shadow_var(--q-motion-normal)_var(--q-ease-out)] hover:[box-shadow:var(--q-shadow-md)] hover:[transform:translateY(-2px)] focus-visible:[box-shadow:var(--q-shadow-md)] focus-visible:[transform:translateY(-2px)] motion-reduce:hover:[transform:none] max-[767px]:shrink-0 max-[767px]:snap-start ${
            phone ? 'w-auto' : 'w-[294px] max-[1199px]:shrink-0 max-[767px]:w-[280px] min-[1200px]:w-auto min-[1200px]:max-w-[294px] min-[1200px]:flex-[1_1_220px]'
          }`}
          href={c.url}
          target="_blank"
          rel="noopener"
          data-vibesite-event="virtual_tour_clicked"
          onClick={modal.open(c.url, c.name)}
          data-reveal
          style={stagger(i)}
        >
          <span
            className={`relative block border-b border-b-[--q-border] bg-[--q-plan-surface] [&_img]:absolute [&_img]:inset-0 [&_img]:h-full [&_img]:w-full ${
              c.isPhoto ? '[&_img]:object-cover' : '[&_img]:object-contain'
            } ${phone ? 'h-[120px]' : 'h-[220px] max-[767px]:h-[190px]'}`}
          >
            {c.img ? <VibesitesImage url={c.img} alt="" sizes={phone ? '50vw' : '300px'} /> : PLAN_OUTLINE}
            <span
              className={`absolute left-1/2 top-1/2 inline-flex items-center justify-center rounded-full bg-[--q-primary] font-semibold text-[--q-on-primary] [transition:transform_var(--q-motion-normal)_var(--q-ease-out)] group-hover/vtcard:[transform:scale(1.08)] motion-reduce:group-hover/vtcard:[transform:none] ${
                phone ? 'm-[-22px_0_0_-22px] h-11 w-11 text-[13px]' : 'm-[-32px_0_0_-32px] h-16 w-16 text-[15px]'
              }`}
              aria-hidden="true"
            >
              3D
            </span>
          </span>
          <span className={`flex flex-col ${phone ? 'gap-0.5 px-3 pb-1 pt-3' : 'gap-1 px-6 py-5'}`}>
            <span className={`font-heading ${phone ? 'text-[20px] leading-[26px]' : 'text-[24px] leading-[30px]'}`}>{c.name}</span>
            <span className={`text-sm leading-5 text-[--q-muted]${phone ? ' hidden' : ''}`}>{c.desc}</span>
            <span className={`textlink ${phone ? '[&.textlink]:inline-flex [&.textlink]:min-h-11 [&.textlink]:items-center' : 'pt-2.5'}`}>
              Take the 3D Tour <span className="btn__arrow group-hover/vtcard:[transform:translateX(4px)]">→</span>
            </span>
          </span>
        </a>
      ))}
    </EditableCollectionBinding>
  );
  const ways = (
    <div className="flex min-w-0 flex-auto flex-col border-t border-t-[--q-ink] min-[1200px]:min-w-[400px]">
      <EditableSlotGroup groupPath="touroptions.tour_cta" definition={tourCtaDef}>
        <div className={way}>
          <div className={wayCopy}>
            <RichTextContent path="touroptions.self_title" as="h3" className={wayTitle} />
            <RichTextContent path="touroptions.self_lead" as="p" className={wayLead} />
          </div>
          <a
            className="btn btn-secondary"
            href={tourLink.href}
            target={tourLink.isTour ? tour._target || '_self' : '_self'}
            data-vibesite-event={tourLink.isTour ? tour.vibesite_event || undefined : undefined}
          >
            {tourLink.label}
          </a>
        </div>
      </EditableSlotGroup>
      {(address || tel) && (
        <div className={way}>
          <div className={wayCopy}>
            <RichTextContent path="touroptions.office_title" as="h3" className={wayTitle} />
            {address && (
              <p className={wayLead}>
                {addressUrl ? (
                  <a className="font-medium text-[--q-primary-on-paper] underline underline-offset-[3px]" href={addressUrl} target="_blank" rel="noopener">
                    {address}
                  </a>
                ) : (
                  address
                )}
              </p>
            )}
          </div>
          {tel && (
            <a className="btn btn-secondary dniphonehref" href={tel}>
              Call <span className="dniphone">{flags.phone}</span>
            </a>
          )}
        </div>
      )}
    </div>
  );

  if (phone) {
    return (
      <section className="bg-[--q-paper] px-[--gutter]" id="tour-options">
        <DetailsRow id="tour" title={<RichTextContent path="touroptions.row_title" as="span" />} summary={rowSummary}>
          <div className="flex flex-col gap-4">
            <RichTextContent path="touroptions.lead" as="p" className={toursLead} defaultValue={leadDefault} />
            {cards.length > 0 && tiles}
            {ways}
          </div>
        </DetailsRow>
        {modal.node}
      </section>
    );
  }

  return (
    <section className="section section--surface [&.section]:py-4xl max-[767px]:[&.section]:py-14" id="tour">
      <div className="flex items-end justify-between gap-16 max-[1199px]:flex-col max-[1199px]:items-start max-[1199px]:gap-4" data-reveal>
        <div className="flex max-w-[640px] flex-col gap-4">
          <RichTextContent path="touroptions.eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="touroptions.title" as="h2" className="h2" />
        </div>
        <RichTextContent path="touroptions.lead" as="p" className={toursLead} defaultValue={leadDefault} />
      </div>
      <div className="mt-12 flex items-start gap-6 max-[1199px]:flex-col max-[1199px]:items-stretch max-[767px]:mt-7">
        {cards.length > 0 && tiles}
        {ways}
      </div>
      {modal.node}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TourOptions',
  displayName: 'Tour Options',
  tokens: {
    'touroptions.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Tour your way</p>' },
    'touroptions.title': { label: 'Title', type: 'richtext', defaultValue: '<p>Walk through before you visit.</p>' },
    'touroptions.lead': {
      label: 'Lead',
      description: 'Until edited, names the plans with 3D tours and points to a self-guided tour; with no tours it drops the 3D sentence.',
      type: 'richtext',
      defaultValue: '<p>Step inside a home in 3D from home, then see one in person on a self-guided tour.</p>',
      generator: {
        prompt: 'One sentence on the ways to see a home here: which plans have 3D walkthroughs, then invite them to see one in person on a self-guided tour. This community has no leasing office, so never mention office visits or office hours.',
        context: 'Lead of the Tour Your Way section on the Floor Plans page, beside the 3D tour tiles and the self-guided tour row.',
        length: '1 sentence',
      },
    },
    'touroptions.self_title': { label: 'Self-Guided Title', type: 'richtext', defaultValue: '<p>Self-guided tour</p>' },
    'touroptions.self_lead': {
      label: 'Self-Guided Text',
      type: 'richtext',
      defaultValue: '<p>Pick an available home and a time, then let yourself in and look around at your own pace.</p>',
    },
    'touroptions.office_title': { label: 'Address Title', type: 'richtext', defaultValue: '<p>Find the community</p>' },
    'touroptions.row_title': { label: 'Row Title (phone)', type: 'richtext', defaultValue: '<p>Tour your way</p>' },
  },
  slotGroups: {
    'touroptions.tour_cta': {
      label: 'Self-Guided Tour Button',
      description: 'Without a self-guided tour URL on the property, the default button becomes Contact Us.',
      fields: {
        label: { label: 'Button Text', type: 'text', defaultValue: 'Book a Self-Guided Tour' },
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
        vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'self_guided_tour_clicked' },
      },
    },
  },
  collectionBindings: {
    'touroptions.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(TourOptions as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TourOptions;
